import { Subject } from '@erp/authz';

// Presentation-only names for the OMS v3 path takeover: the promoted
// subjects read unversioned and their legacy counterparts carry a
// "(Legacy)" suffix so admins can tell the pairs apart. The RBAC subject
// keys keep their versioned names on purpose — other tenants still run the
// legacy flow. The "(Legacy)" rows disappear when the legacy subjects are
// removed at full cutover.
const SUBJECT_LABEL_OVERRIDES: Partial<Record<Subject, string>> = {
  // Reads as what the buttons say on the 844 submissions page, not as the
  // subject key, so admins can find it without knowing the RBAC name.
  [Subject.SD844Resubmission]: '844 Cancel & Resubmit',
  [Subject.McpServer]: 'MCP Server',
  [Subject.APThroughputDashboard]: 'AP Throughput',
  [Subject.SupplierProductMatch]: 'Supplier Product Matches',
  [Subject.InboundEmail]: 'Inbound Emails',
  [Subject.AiCost]: 'AI Cost',
  [Subject.SalesOrderDraftV3]: 'Sales Order Draft',
  [Subject.DiscountV2]: 'Discount',
  [Subject.SalesOrderDraft]: 'Sales Order Draft (Legacy)',
  [Subject.Discount]: 'Discount (Legacy)',
};

export function formatPermissionSubjectLabel(subject: string): string {
  const override = SUBJECT_LABEL_OVERRIDES[subject as Subject];
  if (override) {
    return override;
  }
  return subject
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
}

export type ApiDomainGroup = {
  domain: string;
  subdomains: Array<{ subdomain: string; subjects: Subject[] }>;
};

export function sortApiDomainGroupsForDisplay(
  groups: readonly ApiDomainGroup[],
): ApiDomainGroup[] {
  return [...groups]
    .sort((a, b) =>
      a.domain.localeCompare(b.domain, undefined, { sensitivity: 'base' }),
    )
    .map((d) => ({
      ...d,
      subdomains: [...d.subdomains]
        .sort((a, b) =>
          a.subdomain.localeCompare(b.subdomain, undefined, {
            sensitivity: 'base',
          }),
        )
        .map((s) => ({
          ...s,
          subjects: [...s.subjects].sort((x, y) =>
            formatPermissionSubjectLabel(x).localeCompare(
              formatPermissionSubjectLabel(y),
              undefined,
              { sensitivity: 'base' },
            ),
          ),
        })),
    }));
}

export function filterApiDomainGroupsBySearch(
  groups: readonly ApiDomainGroup[],
  query: string,
): ApiDomainGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return [...groups];
  }
  return groups
    .map((domain) => ({
      ...domain,
      subdomains: domain.subdomains
        .map((sub) => ({
          ...sub,
          subjects: sub.subjects.filter((subject) => {
            const label = formatPermissionSubjectLabel(subject);
            const blob =
              `${label} ${subject} ${domain.domain} ${sub.subdomain}`.toLowerCase();
            return blob.includes(q);
          }),
        }))
        .filter((sub) => sub.subjects.length > 0),
    }))
    .filter((d) => d.subdomains.length > 0);
}
